import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ImageIcon, Phone, Send, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import VehicleSubmissionForm from "@/components/VehicleSubmissionForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const inputCls = "bg-surface-2/60 border-metallic text-white placeholder:text-slate-500";

export default function Salj() {
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", regnummer: "", info: "" });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length + files.length > 6) {
      toast.error("Max 6 foton per inlämning.");
      return;
    }
    const next = [...files, ...picked].slice(0, 6);
    setFiles(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const removeFile = (i) => {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      if (form.email) fd.append("email", form.email);
      fd.append("regnummer", form.regnummer);
      if (form.info) fd.append("info", form.info);
      files.forEach((f) => fd.append("photos", f));
      const { data } = await api.post("/submit-vehicle", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.email_sent) {
        toast.success("Tack! Vi har fått din inlämning och hör av oss så snart vi kan.");
      } else {
        toast.success("Tack! Din inlämning är mottagen. Hör du inte av oss, ring oss gärna.");
      }
      setForm({ name: "", phone: "", email: "", regnummer: "", info: "" });
      setFiles([]);
      setPreviews([]);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-28 md:pt-36 pb-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <p className="font-mono-brand text-xs md:text-sm tracking-[0.3em] text-electric mb-4">SÄLJ TILL OSS</p>
          <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white">
            Sälj din husbil eller <span className="text-gradient-blue">husvagn till oss</span>
          </h1>
          <p className="mt-4 text-slate-400 leading-relaxed">
            Fyll i regnumret så tittar vi på ditt fordon och återkommer med ett prisförslag. Foto och info är
            valfritt — men det gör det lättare för oss att ge dig ett bra pris.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-[1.4fr_1fr] items-start">
          <VehicleSubmissionForm compact />

          <div className="space-y-6">
            <div className="rounded-2xl border border-brand/40 bg-gradient-to-br from-surface to-surface-2/70 p-8" data-testid="salj-info-card">
              <Camera size={24} className="text-electric" />
              <h2 className="mt-4 font-display font-bold text-xl text-white">Så här fungerar det</h2>
              <ol className="mt-4 space-y-3 text-sm text-slate-400 leading-relaxed list-decimal list-inside">
                <li>Du fyller i regnummer och skickar in — med bilder och info om du vill.</li>
                <li>Vi kollar upp fordonet och ringer upp dig med ett prisförslag.</li>
                <li>Är du nöjd bokar vi in värdering och överlämning hos oss i Borås.</li>
              </ol>
            </div>
            <div className="rounded-2xl border border-metallic/70 bg-surface p-8" data-testid="salj-phone-card">
              <Phone size={24} className="text-electric" />
              <h2 className="mt-4 font-display font-bold text-xl text-white">Hellre prata?</h2>
              <a
                href="tel:+46720200996"
                data-testid="salj-phone-link"
                className="mt-2 block font-mono-brand text-lg text-white hover:text-electric transition-colors"
              >
                +46 72 020 09 96
              </a>
              <p className="mt-2 text-sm text-slate-400">Ring så värderar vi tillsammans.</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
